import {
  Box,
  Chip,
  List,
  ListItem,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';
import TimerOffIcon from '@mui/icons-material/TimerOff';
import { AllocationsExpiringItem } from '../types';
import { PersonAvatar } from '../../shared/PersonAvatar';
import { widgetTokens } from '../designTokens';
import { DashboardWidget } from './DashboardWidget';
import { WidgetMoreLink } from './WidgetMoreLink';

interface AllocationsExpiringWidgetProps {
  items?: AllocationsExpiringItem[];
  loading?: boolean;
  onNavigateToPlanning?: () => void;
}

function daysUntil(iso: string): number {
  const end = new Date(iso);
  const now = new Date();
  end.setHours(0, 0, 0, 0);
  now.setHours(0, 0, 0, 0);
  return Math.round((end.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
}

function urgency(days: number): {
  label: string;
  color: 'error' | 'warning' | 'info';
} {
  if (days <= 0) return { label: 'Today', color: 'error' };
  if (days <= 7) return { label: `${days}d left`, color: 'error' };
  if (days <= 14) return { label: `${days}d left`, color: 'warning' };
  return { label: `${days}d left`, color: 'info' };
}

export function AllocationsExpiringWidget({
  items,
  loading,
  onNavigateToPlanning,
}: AllocationsExpiringWidgetProps) {
  const empty = !loading && (!items || items.length === 0);
  const all = items ?? [];
  const visible = all.slice(0, widgetTokens.maxRows);
  const hidden = Math.max(0, all.length - visible.length);

  return (
    <DashboardWidget
      title="Allocations expiring soon"
      icon={<TimerOffIcon fontSize="small" />}
      iconBg="warning.50"
      accentColor="warning.main"
      cta={
        onNavigateToPlanning
          ? { label: 'Plan', onClick: onNavigateToPlanning }
          : undefined
      }
      loading={loading}
      empty={empty}
      emptyMessage="No allocations ending in the next 14 days."
      data-testid="widget-allocations-expiring"
    >
      <List dense disablePadding>
        {visible.map((item) => {
          const days = daysUntil(item.endDate);
          const u = urgency(days);
          return (
            <ListItem key={item.allocationId} disableGutters>
              <ListItemText
                primary={
                  <Stack
                    direction="row"
                    spacing={1}
                    sx={{
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                      <PersonAvatar
                        name={item.employeeName}
                        src={item.avatarUrl}
                        size={22}
                        fontSize="0.625rem"
                      />
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 500 }}
                        noWrap
                      >
                        {item.employeeName}
                      </Typography>
                    </Box>
                    <Chip
                      size="small"
                      color={u.color}
                      variant="outlined"
                      label={u.label}
                      sx={{ height: 20, fontSize: 11 }}
                    />
                  </Stack>
                }
                secondary={
                  <Typography variant="caption" color="text.secondary" noWrap>
                    {item.projectName} · ends{' '}
                    {new Date(item.endDate).toLocaleDateString()}
                  </Typography>
                }
              />
            </ListItem>
          );
        })}
      </List>
      <WidgetMoreLink hiddenCount={hidden} onClick={onNavigateToPlanning} />
    </DashboardWidget>
  );
}
